import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload } from 'lucide-react';
import { uploadContentImage } from '@/lib/upload-content-image';
import { useToast } from '@/components/ui/use-toast';
import { normalizeImageUrl, retryImageOnce } from '@/lib/utils';

interface ContentImageFieldProps {
  value: string;
  onChange: (url: string) => void;
  id?: string;
  label?: string;
  required?: boolean;
}

/**
 * Image field for the admin content forms: paste a URL, or upload a file.
 *
 * Uploading writes two objects to the content-images bucket -- the original as
 * an archive, and a resized <stem>_smaller.webp -- and stores the smaller one,
 * matching what the Dropbox migration produced. Artwork used to be linked from
 * Dropbox, which dropped connections when a catalogue asked for the whole set
 * at once and served megabyte originals into a card a few hundred pixels wide.
 * Anything uploaded here avoids repeating that.
 *
 * The URL box stays: existing content still points at other hosts, and an
 * external link is occasionally what you want.
 */
export function ContentImageField({
  value,
  onChange,
  id = 'imageUrl',
  label = 'Afbeelding',
  required,
}: ContentImageFieldProps) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const { url, originalBytes, smallerBytes } = await uploadContentImage(file);
      onChange(url);
      toast({
        title: 'Afbeelding geüpload',
        description: `${Math.round(originalBytes / 1024)} kB teruggebracht naar ${Math.round(
          smallerBytes / 1024,
        )} kB.`,
      });
    } catch (err) {
      console.error('Content image upload failed:', err);
      toast({
        variant: 'destructive',
        title: 'Uploaden mislukt',
        description: err instanceof Error ? err.message : 'Probeer het opnieuw.',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Plak een URL of upload een afbeelding"
          required={required}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="shrink-0 gap-2"
        >
          <Upload className="h-4 w-4" />
          {uploading ? 'Bezig...' : 'Upload'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>

      {value && (
        <div className="mt-1 overflow-hidden rounded-md border w-full max-w-[220px]">
          <img
            src={normalizeImageUrl(value)}
            alt=""
            className="w-full h-auto"
            onError={retryImageOnce}
          />
        </div>
      )}
    </div>
  );
}
